// <user-menu> Web Component
//
// Phase 1 of CDN caching refactor (memory/cdn_caching_plan.md). Wraps the
// header's authenticated/anonymous user dropdown so the page can be served
// canonical-anonymous from a CDN edge cache and JS hydrates per-user.
//
// Two variants on the page (desktop + mobile menu); each <user-menu>
// instance reads its variant attribute and renders the matching markup.
//
// Public API (consumed by other elements like <save-button>):
//   window.userMenuReady — Promise that resolves ONCE on first hydration
//                          with the user value. Use for first-paint coordination only.
//   window.currentUser   — Mutable getter (read directly). Returns latest
//                          cached user object or null. undefined until first hydration.
//   document 'user-changed' event — Fires on every state transition
//                                   (initial hydrate, 401 re-resolve, bfcache, cross-tab logout).
//                                   Late subscribers should also read window.currentUser.

(function () {
    // ---- Module-level state ------------------------------------------------
    // Promise MUST be created synchronously at script eval time so a <save-button>
    // whose connectedCallback runs before the first <user-menu>'s ever does
    // can still `await window.userMenuReady` correctly.
    let _resolveReady;
    window.userMenuReady = new Promise(function (resolve) { _resolveReady = resolve; });
    window.currentUser = undefined; // not yet hydrated
    let _readyResolved = false;

    // Coalesce concurrent fetches across multiple <user-menu> instances.
    let _inFlight = null;

    function _setUser(user) {
        window.currentUser = user; // null or {id, email, first_name, last_name, initials, genius_level}
        if (!_readyResolved) {
            _readyResolved = true;
            _resolveReady(user);
        }
        document.dispatchEvent(new CustomEvent('user-changed', {
            bubbles: true,
            detail: { user: user },
        }));
    }

    function _fetchMe() {
        // sessionStorage cache (60s TTL) — short window before re-validating.
        try {
            const raw = sessionStorage.getItem('user_me_v1');
            if (raw) {
                const obj = JSON.parse(raw);
                if (obj && (Date.now() - obj.t) < 60000) {
                    return Promise.resolve({ user: obj.user, definitive: true });
                }
                sessionStorage.removeItem('user_me_v1');
            }
        } catch (e) { /* ignore */ }

        if (_inFlight) return _inFlight;
        // /api/auth/session returns 200 in all cases (anon + authed), so
        // browsers don't log a 401 console error on anonymous page loads
        // (PageSpeed Best Practices). Response shape:
        //   { authenticated: bool, user: User|null }
        // user is only valid when authenticated === true.
        _inFlight = fetch('/api/auth/session', { cache: 'no-store', credentials: 'same-origin' })
            .then(function (resp) {
                if (resp.status === 200) {
                    return resp.json().then(function (session) {
                        if (session && session.authenticated === true && session.user) {
                            return { user: session.user, definitive: true };
                        }
                        return { user: null, definitive: true };
                    });
                }
                // 5xx — couldn't determine. Treat as anonymous for state
                // purposes (so dependent elements don't deadlock waiting on
                // userMenuReady) but don't WRITE to sessionStorage so the
                // next request retries. _render path checks `definitive`
                // and skips re-rendering on indeterminate, leaving SSR
                // markup intact. Plan §1.2: "Network error → leave whatever's
                // there (don't churn UI on transient errors)."
                return { user: null, definitive: false };
            })
            .catch(function () {
                // Network error: same indeterminate path as 5xx.
                return { user: null, definitive: false };
            })
            .then(function (result) {
                _inFlight = null;
                if (result.definitive) {
                    try {
                        sessionStorage.setItem('user_me_v1', JSON.stringify({ t: Date.now(), user: result.user }));
                    } catch (e) { /* ignore */ }
                }
                return result;
            });
        return _inFlight;
    }

    function _invalidate() {
        try { sessionStorage.removeItem('user_me_v1'); } catch (e) { /* ignore */ }
        document.querySelectorAll('user-menu').forEach(function (el) { el._refetch(); });
    }

    // Cross-tab logout signal.
    window.addEventListener('storage', function (e) {
        if (e.key === 'auth_change') _invalidate();
    });

    // bfcache restore (back/forward navigation).
    window.addEventListener('pageshow', function (e) {
        if (e.persisted) _invalidate();
    });

    // ---- Markup helpers ----------------------------------------------------
    // Markup matches static/templates/layouts/base.html — keep in sync if
    // the SSR templates change. v6 plan §1.2 acknowledges this duplication.

    function _esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    function _desktopAuthedHTML(user) {
        const initials = _esc(user.initials);
        const firstName = _esc(user.first_name);
        const lastName = _esc(user.last_name);
        const email = _esc(user.email);
        const genius = parseInt(user.genius_level, 10) || 1;
        return '' +
            '<div class="relative" x-data="{userOpen: false}">' +
            '<button type="button" @click="userOpen = !userOpen" class="flex items-center gap-2 px-2 py-1.5 hover:bg-blue-700 rounded transition-colors">' +
                '<div class="w-8 h-8 rounded-full bg-booking-yellow text-booking-blue flex items-center justify-center text-sm font-bold">' + initials + '</div>' +
                '<span class="text-sm font-medium hidden sm:inline">' + firstName + '</span>' +
                '<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>' +
            '</button>' +
            '<div x-show="userOpen" x-cloak @click.away="userOpen = false" x-transition class="absolute right-0 mt-1 w-60 bg-white rounded-lg shadow-lg border z-50 py-1 text-gray-800">' +
                '<div class="px-4 py-3 border-b">' +
                    '<p class="font-semibold text-sm">' + firstName + ' ' + lastName + '</p>' +
                    '<p class="text-xs text-gray-500 truncate">' + email + '</p>' +
                    '<span class="inline-flex items-center gap-1 mt-1.5 bg-yellow-400 text-blue-900 text-[10px] font-bold px-1.5 py-0.5 rounded">' +
                        '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/></svg>' +
                        'Genius Level ' + genius +
                    '</span>' +
                '</div>' +
                '<a href="/account" class="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50">' +
                    '<svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>' +
                    'My account' +
                '</a>' +
                '<a href="/account/bookings" class="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50">' +
                    '<svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>' +
                    'Bookings & Trips' +
                '</a>' +
                '<a href="/account/rewards" class="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50">' +
                    '<svg class="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 24 24"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/></svg>' +
                    'Genius loyalty programme' +
                '</a>' +
                '<a href="/account/rewards" class="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50">' +
                    '<svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>' +
                    'Rewards & Wallet' +
                '</a>' +
                '<a href="/account/reviews" class="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50">' +
                    '<svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>' +
                    'Reviews' +
                '</a>' +
                '<a href="/account/saved" class="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50">' +
                    '<svg class="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>' +
                    'Saved' +
                '</a>' +
                '<div class="border-t my-1"></div>' +
                '<button type="button" data-action="logout" class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left">' +
                    '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>' +
                    'Sign out' +
                '</button>' +
            '</div>' +
            '</div>';
    }

    function _desktopAnonHTML(labels) {
        return '' +
            '<button type="button" data-action="open-register" class="user-menu-register-btn px-3 py-1.5 text-sm border border-white rounded hover:bg-blue-700">' + _esc(labels.register) + '</button>' +
            '<button type="button" data-action="open-signin" class="user-menu-signin-btn px-3 py-1.5 text-sm border border-white rounded hover:bg-blue-700">' + _esc(labels.signin) + '</button>';
    }

    function _mobileAuthedHTML(user, labels) {
        const initials = _esc(user.initials);
        const firstName = _esc(user.first_name);
        const lastName = _esc(user.last_name);
        const genius = parseInt(user.genius_level, 10) || 1;
        return '' +
            '<div class="flex items-center gap-2 px-4 py-2">' +
                '<div class="w-8 h-8 rounded-full bg-booking-yellow text-booking-blue flex items-center justify-center text-sm font-bold">' + initials + '</div>' +
                '<div>' +
                    '<span class="text-sm font-medium">' + firstName + ' ' + lastName + '</span>' +
                    '<span class="inline-flex items-center gap-1 ml-2 bg-yellow-400 text-blue-900 text-[10px] font-bold px-1.5 py-0.5 rounded">' +
                        '<svg class="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/></svg>' +
                        'Genius Level ' + genius +
                    '</span>' +
                '</div>' +
            '</div>' +
            '<div class="px-2 space-y-0.5">' +
                '<a href="/account" class="block px-3 py-2 text-sm rounded hover:bg-blue-700">My account</a>' +
                '<a href="/account/bookings" class="block px-3 py-2 text-sm rounded hover:bg-blue-700">Bookings & Trips</a>' +
                '<a href="/account/rewards" class="block px-3 py-2 text-sm rounded hover:bg-blue-700">Rewards & Wallet</a>' +
                '<a href="/account/saved" class="block px-3 py-2 text-sm rounded hover:bg-blue-700">Saved</a>' +
            '</div>' +
            '<div class="px-4 pt-2">' +
                '<button type="button" data-action="logout" class="block w-full px-4 py-2 text-sm border border-white rounded hover:bg-blue-700 text-center">' + _esc(labels.signout) + '</button>' +
            '</div>';
    }

    function _mobileAnonHTML(labels) {
        return '' +
            '<button type="button" data-action="open-register" class="user-menu-register-btn block px-4 py-2 text-sm border border-white rounded hover:bg-blue-700 text-center">' + _esc(labels.register) + '</button>' +
            '<button type="button" data-action="open-signin" class="user-menu-signin-btn block px-4 py-2 text-sm bg-white text-booking-blue rounded hover:bg-gray-100 text-center font-medium">' + _esc(labels.signin) + '</button>';
    }

    // ---- Custom element ---------------------------------------------------
    class UserMenu extends HTMLElement {
        connectedCallback() {
            if (this._wiredOnce) return;
            this._wiredOnce = true;
            this._labels = {
                signin: this.dataset.labelSignin || 'Sign in',
                register: this.dataset.labelRegister || 'Register',
                signout: this.dataset.labelSignout || 'Sign out',
            };
            // Track what's currently in the DOM so we don't redundantly
            // replace SSR markup on a cache hit (which would destroy
            // mid-interaction Alpine state like userOpen=true).
            this._lastRenderedUserId = this.dataset.ssrUserId || '';
            this._listenForChanges();
            // Defer _hydrate (and the click wiring that depends on existing
            // children) until after the parser has finished inserting SSR
            // children. For parser-inserted custom elements registered via
            // a synchronous <head> script, connectedCallback fires at the
            // OPENING tag — children are not yet in the DOM. A naive
            // setTimeout(0) is NOT sufficient: empirically it fires before
            // the parser appends children when the user_me_v1 sessionStorage
            // cache hits and the Promise resolves on a same-task microtask.
            // DOMContentLoaded fires only after the full document is parsed,
            // guaranteeing children are present so innerHTML replacement
            // cleanly swaps SSR anon → authed markup. Same pattern as
            // <currency-switcher>'s first-paint sync.
            const self = this;
            const run = function () {
                self._wireClicks(); // children now in DOM
                self._hydrate();
            };
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', run, { once: true });
            } else {
                run();
            }
        }

        _variant() {
            return this.getAttribute('variant') === 'mobile' ? 'mobile' : 'desktop';
        }

        _hydrate() {
            const self = this;
            _fetchMe().then(function (result) {
                // ALWAYS resolve userMenuReady so dependent elements
                // (save-button, booking-request-banner) don't deadlock on
                // network/server errors. Indeterminate result is treated as
                // anonymous for state but does NOT replace SSR markup.
                if (result.definitive) {
                    self._render(result.user);
                }
                _setUser(result.user);
            });
        }

        _refetch() {
            const self = this;
            _fetchMe().then(function (result) {
                if (result.definitive) {
                    self._render(result.user);
                }
                if (window.currentUser !== result.user) _setUser(result.user);
            });
        }

        _render(user) {
            const newId = user ? user.id : '';
            // Skip re-render when DOM already matches: avoids destroying
            // Alpine state (e.g. userOpen=true) on cache hits and prevents
            // double-render via the _listenForChanges callback below.
            if (this._lastRenderedUserId === newId) return;
            this._lastRenderedUserId = newId;
            const variant = this._variant();
            if (user) {
                this.innerHTML = variant === 'mobile' ? _mobileAuthedHTML(user, this._labels) : _desktopAuthedHTML(user);
            } else {
                this.innerHTML = variant === 'mobile' ? _mobileAnonHTML(this._labels) : _desktopAnonHTML(this._labels);
            }
            this._wireClicks();
            // Authed Alpine x-data attributes need re-init since we replaced DOM.
            // Best-effort: Alpine.initTree if available.
            if (window.Alpine && typeof window.Alpine.initTree === 'function') {
                try { window.Alpine.initTree(this); } catch (e) { /* ignore */ }
            }
        }

        _wireClicks() {
            const self = this;
            const isMobile = this._variant() === 'mobile';
            // Sign in button → existing global event the header Alpine listens for.
            // On mobile, also close the slide-down drawer that contains the
            // button — matches SSR's `mobileMenuOpen = false` behavior.
            this.querySelectorAll('[data-action="open-signin"]').forEach(function (btn) {
                btn.addEventListener('click', function () {
                    if (isMobile) self._closeMobileDrawer();
                    document.dispatchEvent(new CustomEvent('open-signin', { bubbles: true }));
                });
            });
            // Register button → existing direct-Alpine-state-mutation pattern
            // (matches static/templates/pages/home.html:282 and help.html:431).
            // Defensive guard: never run before Alpine has initialized.
            this.querySelectorAll('[data-action="open-register"]').forEach(function (btn) {
                btn.addEventListener('click', function () {
                    if (isMobile) self._closeMobileDrawer();
                    const h = document.querySelector('header');
                    if (h && h._x_dataStack && h._x_dataStack[0]) {
                        h._x_dataStack[0].showRegister = true;
                        h._x_dataStack[0].authError = '';
                    }
                });
            });
            // Logout buttons (post-hydration markup) reuse the global logout()
            // helper from main.js; an inline-onclick handler in SSR markup
            // already does this, our re-rendered markup uses data-action.
            this.querySelectorAll('[data-action="logout"]').forEach(function (btn) {
                btn.addEventListener('click', function () {
                    if (typeof window.logout === 'function') window.logout();
                });
            });
        }

        _closeMobileDrawer() {
            const h = document.querySelector('header');
            if (h && h._x_dataStack && h._x_dataStack[0]) {
                h._x_dataStack[0].mobileMenuOpen = false;
            }
        }

        _listenForChanges() {
            const self = this;
            document.addEventListener('user-changed', function (e) {
                // _render is idempotent (early-returns when DOM matches),
                // so it's safe to fire on every event.
                const newUser = e && e.detail ? e.detail.user : null;
                self._render(newUser);
            });
        }
    }

    if (!customElements.get('user-menu')) {
        customElements.define('user-menu', UserMenu);
    }
})();
